<template>
  <div class="rag-carrier-demo">
    <!-- Header Section -->
    <div class="header-section">
      <div class="container">
        <h1 class="main-title">
          <i class="fas fa-building"></i>
          Carrier & Policy Intelligence
        </h1>
        <p class="subtitle">Analyze carriers (NAIC, A.M. Best), agents, premiums, endorsements, and limits. Portfolio sizing by policy type.</p>
        <div class="controls">
          <input v-model.number="projectId" type="number" placeholder="Project ID" />
          <button @click="loadResults" :disabled="loading">
            <span v-if="loading">Loading…</span>
            <span v-else>Load Results</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Summary Bar -->
    <div class="dashboard-section">
      <div class="container">
        <div class="metrics-bar" v-if="summary">
          <div class="metric-card">
            <div class="metric-value">{{ summary.total_documents }}</div>
            <div class="metric-label">Documents</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">{{ (summary.overall_compliance_rate * 100).toFixed(1) }}%</div>
            <div class="metric-label">Avg Compliance</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">{{ Object.keys(carrierStats).length }}</div>
            <div class="metric-label">Carriers</div>
          </div>
        </div>

        <div v-if="error" class="notification error">{{ error }}</div>

        <!-- Main Grid -->
        <div class="dashboard-grid">
          <!-- Carriers Card -->
          <div class="dashboard-card">
            <div class="card-header">
              <h3><i class="fas fa-industry"></i> Carriers</h3>
            </div>
            <div class="card-content">
              <div class="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Carrier</th>
                      <th>NAIC</th>
                      <th>A.M. Best</th>
                      <th>Policies</th>
                      <th>Premium (sum)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(c, name) in carrierStats" :key="name">
                      <td>{{ name }}</td>
                      <td>{{ c.naic || '-' }}</td>
                      <td>{{ c.amBest || '-' }}</td>
                      <td>{{ c.count }}</td>
                      <td>{{ formatCurrency(c.totalPremium) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Agents Card -->
          <div class="dashboard-card">
            <div class="card-header">
              <h3><i class="fas fa-user-tie"></i> Agents</h3>
            </div>
            <div class="card-content">
              <ul class="list">
                <li v-for="(a, idx) in topAgents" :key="idx">
                  <strong>{{ a.name }}</strong> — {{ a.email || 'n/a' }} ({{ a.count }} policies)
                </li>
              </ul>
            </div>
          </div>

          <!-- Endorsements Card -->
          <div class="dashboard-card">
            <div class="card-header">
              <h3><i class="fas fa-file-signature"></i> Endorsements</h3>
            </div>
            <div class="card-content">
              <ul class="list">
                <li v-for="(count, code) in endorsementCounts" :key="code">
                  <code>{{ code }}</code> — {{ count }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Policy Limits & Premiums -->
          <div class="dashboard-card full-width">
            <div class="card-header">
              <h3><i class="fas fa-list"></i> Policy Limits & Premiums by Type</h3>
            </div>
            <div class="card-content">
              <div class="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Policy Type</th>
                      <th>Per Occurrence (avg)</th>
                      <th>Aggregate (avg)</th>
                      <th>Premium (avg)</th>
                      <th>Docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(s, type) in policyTypeStats" :key="type">
                      <td>{{ type }}</td>
                      <td>{{ formatCurrency(s.avgEach) }}</td>
                      <td>{{ formatCurrency(s.avgAgg) }}</td>
                      <td>{{ formatCurrency(s.avgPremium) }}</td>
                      <td>{{ s.count }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Portfolio Sizing -->
          <div class="dashboard-card full-width">
            <div class="card-header">
              <h3><i class="fas fa-chart-line"></i> Portfolio Sizing</h3>
            </div>
            <div class="card-content">
              <div class="portfolio">
                <div v-for="(s, type) in policyTypeStats" :key="type" class="portfolio-item">
                  <div class="label">{{ type }}</div>
                  <div class="value">Per-store premium est: {{ formatCurrency(s.avgPremium) }}</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'RAGCarrierFocusedDemo',
  data() {
    return {
      projectId: 6,
      loading: false,
      error: '',
      results: [],
      summary: null,
      carrierStats: {},
      endorsementCounts: {},
      policyTypeStats: {},
    }
  },
  computed: {
    topAgents() {
      const map = {}
      for (const r of this.results) {
        const ctx = r.document_context || {}
        const contacts = ctx.agent_contacts || []
        for (const c of contacts) {
          const key = (c.email || c.name || '').toLowerCase()
          if (!key) continue
          if (!map[key]) map[key] = { name: c.name || key, email: c.email, count: 0 }
          map[key].count += 1
        }
      }
      return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 10)
    }
  },
  methods: {
    async loadResults() {
      this.loading = true
      this.error = ''
      try {
        const res = await api.get(`/projects/${this.projectId}/rag/results/`)
        this.results = res.data || []
        this.summary = {
          total_documents: this.results.length,
          overall_compliance_rate: this._avg(this.results.map(r => r.compliance_score || 0))
        }
        this._buildCarrierStats()
        this._buildEndorsements()
        this._buildPolicyTypeStats()
      } catch (e) {
        this.error = e?.response?.data?.error || e.message
      } finally {
        this.loading = false
      }
    },
    _buildCarrierStats() {
      const stats = {}
      for (const r of this.results) {
        const ctx = r.document_context || {}
        const carrier = (ctx.carrier_details && ctx.carrier_details.carrier_name) || (ctx.key_entities && ctx.key_entities.insurer) || 'Unknown'
        const naic = ctx.carrier_details?.naic_number
        const amBest = ctx.carrier_details?.am_best_rating
        const premiumTotal = Number(ctx.premium_information?.total_premium || 0)
        if (!stats[carrier]) stats[carrier] = { count: 0, naic: naic, amBest: amBest, totalPremium: 0 }
        stats[carrier].count += 1
        stats[carrier].totalPremium += isNaN(premiumTotal) ? 0 : premiumTotal
        if (!stats[carrier].naic && naic) stats[carrier].naic = naic
        if (!stats[carrier].amBest && amBest) stats[carrier].amBest = amBest
      }
      this.carrierStats = stats
    },
    _buildEndorsements() {
      const counts = {}
      for (const r of this.results) {
        const ctx = r.document_context || {}
        const endorsements = ctx.endorsements || []
        for (const e of endorsements) {
          const code = e.code || e.description || 'UNKNOWN'
          counts[code] = (counts[code] || 0) + 1
        }
      }
      this.endorsementCounts = counts
    },
    _buildPolicyTypeStats() {
      const byType = {}
      for (const r of this.results) {
        const ctx = r.document_context || {}
        const types = ctx.coverage_types || r.coverage_types || []
        const fin = ctx.financial_values || {}
        const each = Number(fin.general_liability_limit || fin.each_occurrence || 0)
        const agg = Number(fin.aggregate_limit || 0)
        const premium = Number(ctx.premium_information?.total_premium || 0)
        const typeKey = types[0] || 'unknown'
        if (!byType[typeKey]) byType[typeKey] = { count: 0, sumEach: 0, sumAgg: 0, sumPremium: 0 }
        byType[typeKey].count += 1
        byType[typeKey].sumEach += isNaN(each) ? 0 : each
        byType[typeKey].sumAgg += isNaN(agg) ? 0 : agg
        byType[typeKey].sumPremium += isNaN(premium) ? 0 : premium
      }
      const stats = {}
      for (const [type, s] of Object.entries(byType)) {
        stats[type] = {
          count: s.count,
          avgEach: s.count ? s.sumEach / s.count : 0,
          avgAgg: s.count ? s.sumAgg / s.count : 0,
          avgPremium: s.count ? s.sumPremium / s.count : 0,
        }
      }
      this.policyTypeStats = stats
    },
    _avg(arr) {
      if (!arr.length) return 0
      return arr.reduce((a, b) => a + b, 0) / arr.length
    },
    formatCurrency(n) {
      if (!n) return '$0'
      try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) } catch (_) { return `$${Number(n).toFixed(2)}` }
    }
  },
  mounted() {
    this.loadResults()
  }
}
</script>

<style scoped>
.rag-carrier-demo { min-height: 100vh; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.header-section { background: rgba(255, 255, 255, 0.1); backdrop-filter: blur(10px); padding: 2rem 0; border-bottom: 1px solid rgba(255, 255, 255, 0.2); }
.container { max-width: 1200px; margin: 0 auto; padding: 0 1rem; }
.main-title { color: white; font-size: 2rem; margin: 0 0 .25rem 0; font-weight: 700; display: flex; align-items: center; gap: .6rem; }
.subtitle { color: rgba(255, 255, 255, 0.85); margin: .25rem 0 1rem; }
.controls { display: flex; gap: .5rem; align-items: center; }
.controls input { padding: .5rem .75rem; border-radius: 8px; border: 1px solid #d1d5db; min-width: 140px; }
.controls button { background: #2563eb; color: white; border: none; padding: .5rem .9rem; border-radius: 8px; cursor: pointer; }
.controls button[disabled] { opacity: .6; cursor: not-allowed; }

.dashboard-section { padding: 2rem 0; }
.metrics-bar { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; }
.metric-card { background: rgba(255, 255, 255, 0.15); backdrop-filter: blur(10px); padding: 1rem; border-radius: 12px; text-align: center; border: 1px solid rgba(255, 255, 255, 0.2); }
.metric-value { font-size: 1.6rem; font-weight: bold; color: #ffd700; }
.metric-label { color: rgba(255, 255, 255, 0.95); }

.notification.error { background: #fee2e2; color: #991b1b; padding: .75rem 1rem; border-radius: 8px; border: 1px solid #fecaca; margin-bottom: 1rem; }

.dashboard-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(350px, 1fr)); gap: 1.25rem; }
.dashboard-card { background: white; border-radius: 16px; box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1); overflow: hidden; transition: transform .3s ease, box-shadow .3s ease; }
.dashboard-card:hover { transform: translateY(-4px); box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15); }
.dashboard-card.full-width { grid-column: 1 / -1; }
.card-header { background: linear-gradient(135deg, #2c3e50, #3498db); color: white; padding: 1rem 1.25rem; }
.card-header h3 { margin: 0; font-size: 1.05rem; display: flex; align-items: center; gap: .5rem; }
.card-content { padding: 1.25rem; }

.table-wrapper { overflow: auto; }
.table-wrapper table { width: 100%; border-collapse: collapse; }
.table-wrapper th, .table-wrapper td { text-align: left; padding: .6rem .75rem; border-bottom: 1px solid #e5e7eb; }
.table-wrapper thead th { background: #f3f4f6; font-weight: 600; }

.list { margin: 0; padding-left: 1rem; }
.list li { margin: .4rem 0; }

.portfolio { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: .75rem; }
.portfolio-item { background: #f9fafb; padding: .9rem; border-radius: 8px; border: 1px solid #e5e7eb; }

@media (max-width: 768px) {
  .main-title { font-size: 1.6rem; }
  .dashboard-grid { grid-template-columns: 1fr; }
}
</style>